import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { placed } from '@/lib/style-guard'

type OutcomeTone = 'success' | 'error' | 'warning' | 'info'

const toneClass: Record<OutcomeTone, string> = {
  success: 'bg-[var(--status-success-bg)] text-[var(--status-success-text)]',
  error: 'bg-[var(--status-error-bg)] text-[var(--status-error-text)]',
  warning: 'bg-[var(--status-warning-bg)] text-[var(--status-warning-text)]',
  info: 'bg-[var(--status-info-bg)] text-[var(--status-info-text)]',
}

/**
 * OutcomeCard — the shared treatment for a surface whose whole job is to REPORT
 * what happened: link sent, link expired, email verified, invite accepted,
 * payment confirmed, the one-click token gate.
 *
 * A tone-tinted icon medallion, a page-level heading + optional description,
 * then whatever actions the caller passes as children.
 *
 * Distinct from its neighbours, deliberately:
 *  • `StatusCard` is a LIST ITEM (tinted header + body) — a different shape.
 *  • `EmptyState` is "no data in this region" — h3, neutral medallion, no tone.
 *    Here the tone IS the message (success vs error), and the heading is the
 *    page's h1.
 *
 * Uses the THEME text tokens rather than the always-dark `--text-public-*` pair,
 * so one card serves every context: correct on a light app surface, and
 * pixel-identical inside the auth card — AuthLayout applies `.dark`, where
 * `--text-primary`/`--text-secondary` resolve to the same hex as the public pair
 * (#F3F5F8 / #A8B0BE). The public pair is only needed for always-dark chrome
 * sitting in a *light* document (PublicHeader, SiteFooter), which this is not.
 */
export function OutcomeCard({
  icon: Icon,
  tone = 'info',
  title,
  description,
  children,
  className,
}: {
  icon: LucideIcon
  tone?: OutcomeTone
  title: string
  description?: React.ReactNode
  children?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('text-center', placed(className, 'OutcomeCard'))}>
      <div
        className={cn(
          'mx-auto mb-5 flex size-12 items-center justify-center rounded-full',
          toneClass[tone]
        )}
        aria-hidden="true"
      >
        <Icon className="size-6" />
      </div>
      <h1 className="text-h1 text-[var(--text-primary)] hyphens-auto text-balance">{title}</h1>
      {/* ⚠️ `aria-live`, because this line CHANGES. `NotFound`'s token gate swaps
          it from «lenken er utløpt» to «vi har spurt» when she asks for a new
          one, and that swap was the entire feedback for the click — no toast,
          no region, nothing announced. A sighted person saw the sentence
          change; a screen-reader user pressed a button and got silence.
          Polite, and on the shared shell rather than on `NotFound`, so every
          outcome that ever rewrites its description is covered by
          construction. Inert when the text never changes. */}
      {description && (
        <p aria-live="polite" className="mt-2 text-body text-[var(--text-secondary)]">{description}</p>
      )}
      {/* Buttons passed side by side get a gap of their own (03.10.2026 re-score: two actions
          touched at x≈893 on every guard). A wrapping <div> child, as TokenGate passes, keeps the
          full row it always had. */}
      {children && <div className="mt-6 flex flex-wrap justify-center gap-3 [&>div]:basis-full">{children}</div>}
    </div>
  )
}
